/**
 * t213u: the phone calendar agenda. A month at a time: a strip of the month's days (a dot where something is on), then
 * the days with items in order. Events always; shifts and time off are optional toggles. Platform-neutral.
 */
import { DateTime } from 'luxon';
import { CALENDAR_LAYERS, DEFAULT_CALENDAR_FILTERS, type CalendarFilters, type CalendarItem, type CalendarLayer, type TaskScope } from '../calendar';

export const PHONE_EVENT_LAYERS: CalendarLayer[] = ['company', 'team', 'building', 'personal', 'birthdays', 'closures'];
export interface PhoneCalToggles { shifts: boolean; timeoff: boolean }
export const PHONE_CAL_KEY = 'collab.m.calendar';

export function phoneCalLayers(t: PhoneCalToggles): CalendarLayer[] {
  return [...PHONE_EVENT_LAYERS, ...(t.shifts ? ['shifts' as const] : []), ...(t.timeoff ? ['absences' as const] : [])];
}
/** The month's days (ISO), and the query range: first day to the day after the last. */
export function monthDays(month: string): { days: string[]; from: string; to: string } {
  const start = DateTime.fromISO(month).startOf('month');
  const n = start.daysInMonth ?? 30;
  const days = Array.from({ length: n }, (_, i) => start.plus({ days: i }).toISODate()!);
  return { days, from: days[0]!, to: start.plus({ months: 1 }).toISODate()! };
}
export const shiftMonth = (month: string, dir: -1 | 1) => DateTime.fromISO(month).startOf('month').plus({ months: dir }).toISODate()!;
export const monthTitle = (month: string) => DateTime.fromISO(month).toFormat('LLLL yyyy');

/** Local days an item covers (all-day end is exclusive), in Europe/London. */
export function calItemDays(it: CalendarItem, zone = 'Europe/London'): string[] {
  const s = it.all_day ? DateTime.fromISO(it.start, { zone }) : DateTime.fromISO(it.start).setZone(zone);
  let e = it.all_day ? DateTime.fromISO(it.end, { zone }).minus({ days: 1 }) : DateTime.fromISO(it.end).setZone(zone).minus({ milliseconds: 1 });
  if (e < s) e = s;
  const out: string[] = [];
  for (let d = s.startOf('day'); d <= e && out.length < 62; d = d.plus({ days: 1 })) out.push(d.toISODate()!);
  return out;
}
/** Days of the month with their items (all-day first, then by start), only days that have any. */
export function agendaByDay(items: CalendarItem[], days: string[]): { day: string; items: CalendarItem[] }[] {
  const set = new Set(days);
  const by = new Map<string, CalendarItem[]>();
  for (const it of items) for (const d of calItemDays(it)) if (set.has(d)) (by.get(d) ?? by.set(d, []).get(d)!).push(it);
  return days.filter(d => by.has(d)).map(d => ({
    day: d,
    items: by.get(d)!.sort((a, b) => Number(b.all_day) - Number(a.all_day) || a.start.localeCompare(b.start) || a.title.localeCompare(b.title)),
  }));
}
/** Where an agenda row goes: the event page, else the item's own link. */
export function calItemHref(it: CalendarItem): string | null {
  if (it.event_id) return `/calendar/events/${it.event_id}${it.occurrence ? `?occ=${encodeURIComponent(it.occurrence)}` : ''}`;
  // t349u: a shift opens that shift (its own page, with Back), not the whole My shifts list.
  const shift = it.layer === 'shifts' ? /^shift:([0-9a-f-]{36})$/i.exec(it.id)?.[1] : undefined;
  if (shift) return `/workforce/my-shifts/${shift}`;
  return it.link ?? null;
}
export function calTimeLabel(it: CalendarItem, zone = 'Europe/London'): string {
  if (it.all_day) return 'All day';
  return DateTime.fromISO(it.start).setZone(zone).toFormat('HH:mm');
}

/** t335u item 8: the phone calendar's views (Agenda, Day, Week, Month), remembered per device. */
export type PhoneCalView = 'agenda' | 'day' | 'week' | 'month';
export const PHONE_CAL_VIEWS: { id: PhoneCalView; label: string }[] = [{ id: 'agenda', label: 'Agenda' }, { id: 'day', label: 'Day' }, { id: 'week', label: 'Week' }, { id: 'month', label: 'Month' }];
export const PHONE_CAL_VIEW_KEY = 'hub.phone-cal-view';
export const isPhoneCalView = (v: unknown): v is PhoneCalView => v === 'agenda' || v === 'day' || v === 'week' || v === 'month';
/** The month grid: whole weeks, Monday first, padded with the days either side (5 or 6 rows). */
export function monthGrid(month: string): string[][] {
  const start = DateTime.fromISO(month).startOf('month');
  let d = start.minus({ days: start.weekday - 1 });
  const end = start.endOf('month');
  const weeks: string[][] = [];
  while (d <= end) { weeks.push(Array.from({ length: 7 }, (_, i) => d.plus({ days: i }).toISODate()!)); d = d.plus({ days: 7 }); }
  return weeks;
}
/** The week (Monday to Sunday) holding `day`, and the query range (Monday to the next Monday). */
export function weekOf(day: string): { days: string[]; from: string; to: string } {
  const mon = DateTime.fromISO(day).startOf('week');
  const days = Array.from({ length: 7 }, (_, i) => mon.plus({ days: i }).toISODate()!);
  return { days, from: days[0]!, to: mon.plus({ days: 7 }).toISODate()! };
}
export const shiftWeek = (day: string, dir: -1 | 1) => DateTime.fromISO(day).plus({ weeks: dir }).toISODate()!;
/** "19–25 Oct 2026", "26 Oct – 1 Nov 2026", "28 Dec 2026 – 3 Jan 2027". */
export function weekTitle(day: string): string {
  const { days } = weekOf(day);
  const a = DateTime.fromISO(days[0]!), b = DateTime.fromISO(days[6]!);
  if (a.year !== b.year) return `${a.toFormat('d LLL yyyy')} – ${b.toFormat('d LLL yyyy')}`;
  if (a.month !== b.month) return `${a.toFormat('d LLL')} – ${b.toFormat('d LLL yyyy')}`;
  return `${a.day}–${b.toFormat('d LLL yyyy')}`;
}
/** Up to `max` distinct item colours per day, for the month grid's dots. */
export function dayDots(items: CalendarItem[], days: string[], max = 3): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const g of agendaByDay(items, days)) out.set(g.day, [...new Set(g.items.map(i => i.color || '#64748b'))].slice(0, max));
  return out;
}

/**
 * t337u: the phone calendar's filters are the desktop's (Calendar › Filters): the layers (with All / None), whose task
 * due dates, building, team and saved views. They are the same saved settings, so a change on the phone shows on the
 * computer and the other way round; the phone keeps its own view (Agenda, Day, Week, Month).
 */
export const CAL_TASK_SCOPE_LABEL: Record<TaskScope, string> = { mine: 'Assigned to me', teams: 'Me and my teams', all: 'All tasks I can see' };
export const CAL_TASK_SCOPES: TaskScope[] = ['mine', 'teams', 'all'];
/** Saved filters with the defaults filled in (an older save can miss a field). */
export const phoneCalFilters = (saved: Partial<CalendarFilters> | null | undefined): CalendarFilters => ({ ...DEFAULT_CALENDAR_FILTERS, ...(saved ?? {}) });
/** How many filters differ from the defaults (the yellow dot on the filter button shows when any do). */
export function calFilterCount(f: CalendarFilters): number {
  const d = DEFAULT_CALENDAR_FILTERS;
  const layersDiffer = f.layers.length !== d.layers.length || f.layers.some(l => !d.layers.includes(l));
  return Number(layersDiffer) + Number((f.tasks ?? 'mine') !== d.tasks && f.layers.includes('tasks')) + Number(Boolean(f.location_id)) + Number(Boolean(f.team_id));
}
/** Reset keeps the saved view (the desktop's Month, Week…), only the filters go back. */
export const resetCalFilters = (f: CalendarFilters): CalendarFilters => ({ ...DEFAULT_CALENDAR_FILTERS, view: f.view ?? DEFAULT_CALENDAR_FILTERS.view, location_id: null, team_id: null });
/** Toggle one layer, keeping the desktop's order. */
export const toggleCalLayer = (f: CalendarFilters, l: CalendarLayer): CalendarFilters =>
  ({ ...f, layers: f.layers.includes(l) ? f.layers.filter(x => x !== l) : CALENDAR_LAYERS.filter(x => x === l || f.layers.includes(x)) });
/** A saved view applied on the phone: its filters, but the phone keeps its own view. */
export const applyCalPreset = (cur: CalendarFilters, p: { filters: Partial<CalendarFilters> }): CalendarFilters => ({ ...phoneCalFilters(p.filters), view: cur.view });
/** The calendar query for these filters (as the desktop sends it). */
export const calFilterQuery = (f: CalendarFilters) => ({ layers: f.layers, tasks: f.tasks, location_id: f.location_id ?? null, team_id: f.team_id ?? null });
/** Same filters? (to skip saving what hasn't changed) */
export const sameCalFilters = (a: CalendarFilters, b: CalendarFilters) =>
  a.layers.join() === b.layers.join() && a.tasks === b.tasks && (a.location_id ?? null) === (b.location_id ?? null) && (a.team_id ?? null) === (b.team_id ?? null);
